import { memo, useCallback } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import { useDrag } from 'react-dnd';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import { AlertWithReferences } from '@/components/atoms/AlertWithReferences/AlertWithReferences';
import ActionPlaceholder from '@/components/organisms/Dialogs/Action/Placeholder/Placeholder';
import ConditionPlaceholder from '@/components/organisms/Dialogs/ConditionPlaceholder/ConditionPlaceholder';
import DialogTooltip from '@/components/organisms/Dialogs/DialogTooltip/DialogTooltip';
import BranchList from '@/components/organisms/Dialogs/Node/BranchList/BranchList';
import NodePlaceholder from '@/components/organisms/Dialogs/Node/Placeholder/Placeholder';
import RequisitePlaceholder from '@/components/organisms/Dialogs/Node/RequisitePlaceholder';
import { TimerBranchLabel } from '@/components/organisms/Dialogs/Node/TimerBranchLabel/TimerBranchLabel';
import { MODAL_DELETE, MODAL_WARN } from '@/components/organisms/Modals/ModalConductor';
import { useIsStartingNode } from '@/hooks/useIsStartingNode';
import useNodesConnected, { buildUsedNodeReferences } from '@/hooks/useNodesConnected';
import { cn } from '@/lib/utils';
import { selectHasError } from '@/redux/dialogAlerts/selectors';
import { TYPES, isFinalAction } from '@/redux/dialogs/helper';
import { nodeSelector } from '@/redux/dialogs/selectors';
import { popModal, pushModal } from '@/redux/modals/actions';
import { removeNode, selectNode, convertNode } from '@/redux/nodes/actions';
import { selectIsIdInFlow, selectIsNodeConditionRequisiteSelected } from '@/redux/nodes/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions as permissionActions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { isKeyEnter, preventClickThrough } from '@/util/util';

import ConditionList from './Conditions/ConditionList/ConditionList';
import Intent from './Intent';
import RequisiteList from './RequisiteList';
import { StartingNodeBadge } from './StartingNodeBadge/StartingNodeBadge';
import { BoxErrorIcon } from '../Action/Action/Action';
import ActionList from '../Action/ActionList';
import Box from '../Box/Box';
import NodeHeader from '../NodeHeader/NodeHeader';

import styles from './Node.module.scss';

type Props = { nodeId: string; showBranch?: boolean };

const Node = memo(({ nodeId, showBranch = false }: Props) => {
  const { t } = useTranslation();
  const { slug, brainId } = useParams();
  const navigate = useNavigate();
  const { findUsedNodes } = useNodesConnected();
  const dispatch = useDispatch();
  const hasDialogError = useSelector(selectHasError(nodeId));
  const location = useLocation();
  const isDialogPreview = location.pathname.endsWith('/setup');
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', permissionActions.WRITE)
  );

  const {
    name,
    type,
    parentId,
    showPlaceholder,
    isSelected,
    intent,
    node,
    lastActionType,
    isRootNodeUnknown,
    hasConditions,
  } = useSelector((state) => nodeSelector(state, nodeId), isEqual);

  const isStartingNode = useIsStartingNode(nodeId);

  const isActionInFlow = useSelector(selectIsIdInFlow(nodeId));
  const isAnythingSelected = useSelector(selectIsNodeConditionRequisiteSelected);
  // If the node has no conditions, it is the last node in the flow
  const isLast = !hasConditions;

  const [{ isDragging }, dragRef] = useDrag({
    item: { nodeId, move: true, parentId },
    type: TYPES.NODE,
    canDrag: () => Boolean(parentId) && !node.is_reminder,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      preventClickThrough(e);
      dispatch(selectNode({ nodeId }));
    },
    [dispatch, nodeId]
  );
  const handleRefClick = useCallback(
    (href: string) => {
      dispatch(popModal());
      navigate(href);
    },
    [dispatch, navigate]
  );

  const onDelete = useCallback(() => {
    const usedNodes = findUsedNodes(nodeId);

    if (usedNodes && !isEmpty(usedNodes)) {
      const references = buildUsedNodeReferences(usedNodes, slug, brainId, t);
      const warning = (
        <AlertWithReferences
          references={references}
          referenceProp="href"
          onRefClick={handleRefClick}
        >
          <Trans i18nKey="dialog.delete_node" values={[name]} />
        </AlertWithReferences>
      );
      const warnProps = {
        title: t('common.warning'),
        children: warning,
        primaryButtonText: t('common.close'),
      };

      dispatch(pushModal(MODAL_WARN, warnProps));
    } else {
      const deleteProps = {
        title: t('dialog.delete_node_title'),
        subtitle: <Trans i18nKey="dialog.delete_node_warn" values={[name]} />,
        onDelete: () => {
          dispatch(removeNode({ nodeId }));
          dispatch(popModal());
        },
      };

      dispatch(pushModal(MODAL_DELETE, deleteProps));
    }
  }, [brainId, dispatch, findUsedNodes, handleRefClick, name, nodeId, slug, t]);

  const onKeyUp = useCallback(
    (e) => {
      if (isRootNodeUnknown) {
        return;
      }
      preventClickThrough(e);
      if (isKeyEnter(e)) {
        dispatch(selectNode({ nodeId }));
      } else if (e.key === 'Delete' && canWrite) {
        onDelete();
      }
    },
    [canWrite, dispatch, isRootNodeUnknown, nodeId, onDelete]
  );

  const handleIntentNavigate = useCallback(() => {
    navigate(`/${slug}/ai-agents/${brainId}/workflows/intents/${intent}`);
  }, [brainId, intent, navigate, slug]);

  const handleTriggerConvert = useCallback(() => {
    dispatch(convertNode({ nodeId, node }));
  }, [dispatch, node, nodeId]);

  // This will prevent the reminder nodes to have all the interactivity
  const clickableProps = !node.is_reminder ? { role: 'button', tabIndex: 0, onKeyUp, onClick } : {};

  return (
    <section
      id={nodeId}
      className={cn(styles.node, {
        [styles['node--preview']]: isDialogPreview,
        [styles['node--reminder']]: node.is_reminder,
      })}
      inert={isDialogPreview ? true : undefined}
    >
      {!parentId && !isRootNodeUnknown && <NodePlaceholder />}
      {/*
        Positioning context for the starting-node badge. The badge has to live outside
        DialogTooltip's anchor — as a descendant it would drive the node's hover toolbar,
        and a child cannot cancel that (React fires mouseenter ancestor-first). The node
        box's top margin moves up here so the badge stays centred on the box.
      */}
      <div className={cn('relative', node.is_reminder ? 'mt-3.5' : 'mt-2')}>
        {isStartingNode && <StartingNodeBadge />}
        <DialogTooltip
          onNavigate={type === 'intent' ? handleIntentNavigate : null}
          onTriggerConvert={
            type === 'intent' || (type === 'event' && !node?.is_reminder)
              ? handleTriggerConvert
              : null
          }
          type={type}
          show={true}
        >
          <div
            ref={(node) => {
              dragRef(node);
            }}
            className={cn('dialog-node-tile mt-0!', {
              [styles['node-box__container']]: true,
              [styles['node-box__container--is-dragging']]: isDragging,
              [styles['node-box__container__arrow']]: parentId,
              [styles['node-box__container--reminder']]: node.is_reminder,
            })}
          >
            {hasDialogError && <BoxErrorIcon />}

            <Box
              className={cn({
                [styles.selected]: isSelected,
                [styles.opacity]: !isActionInFlow && !isAnythingSelected,
                [styles.startingNode]: isStartingNode,
              })}
              data-testid="dialog-node"
              {...clickableProps}
            >
              <NodeHeader
                nodeId={nodeId}
                type={type}
                isRootNodeUnknown={isRootNodeUnknown}
                node={node}
                isSelected={isSelected}
              />
              {node?.is_reminder && <TimerBranchLabel nodeId={nodeId} />}
              {type === 'intent' && (
                <div className={styles.node__content}>
                  <Intent nodeId={nodeId} />
                </div>
              )}
            </Box>
            {showBranch && !node.is_reminder && <NodePlaceholder parentId={parentId} branch />}
          </div>
        </DialogTooltip>
      </div>
      <RequisitePlaceholder requisiteIndex={0} nodeId={nodeId} />
      <RequisiteList nodeId={nodeId} />
      <ActionList nodeId={nodeId} />
      {!isFinalAction(lastActionType) && (
        <>
          <ActionPlaceholder nodeId={nodeId} show={showPlaceholder} isLast={isLast} />
          <ConditionPlaceholder parentId={nodeId} />
          <ConditionList nodeId={nodeId} />
        </>
      )}
      <BranchList nodeId={nodeId} />
    </section>
  );
});

Node.displayName = 'Node';

export default Node;
